import * as React from 'react';

import { Information, Warning, WarningAlt } from '@mint-lib/icons';
import { cn } from '@mint-lib/tw-utils';
import { type VariantProps } from 'class-variance-authority';

import { useCharacterLimit } from '../hooks/useCharacterLimit.js';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from './input-group.jsx';
import { Label } from './label.jsx';
import type { PopoverPortal } from './popover.jsx';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip.jsx';
import { typographyVariants } from './typography.jsx';
import { inputVariants } from './variants.jsx';

type DataAttributes = {
  [K in `data-${string}`]?: string | number | boolean | undefined;
};
export interface InputProps
  extends
    Omit<React.ComponentProps<'input'>, 'size' | 'color'>,
    VariantProps<typeof inputVariants>,
    DataAttributes {}

function Input({
  className,
  type,
  size,
  disabled = false,
  color,
  defaultValue,
  onChange,
  value,
  ...props
}: InputProps) {
  return (
    <input
      type={type}
      data-slot="input"
      className={cn(inputVariants({ className, size, color }))}
      disabled={disabled}
      defaultValue={defaultValue}
      onChange={onChange}
      value={value}
      {...props}
    />
  );
}

function InputWithCounter({
  className,
  size,
  disabled = false,
  onChange,
  id,
  maxLength,
  ...props
}: InputProps & {
  maxLength: number;
}) {
  const {
    value,
    characterCount,
    maxLength: limit,
    handleChange,
  } = useCharacterLimit({
    maxLength,
    value: (props.value as string) ?? '',
    onChange,
  });
  return (
    <InputGroup
      className={className}
      size={size}
      color={props.color ?? 'default'}
    >
      <InputGroupInput
        id={id}
        data-length={limit}
        size={size}
        disabled={disabled}
        value={value}
        readOnly={props.readOnly}
        onChange={handleChange}
        maxLength={limit}
        {...props}
      />
      <InputGroupAddon align="inline-end">
        <InputGroupText id={`${id}-description`}>
          {characterCount}/{limit}
        </InputGroupText>
      </InputGroupAddon>
    </InputGroup>
  );
}

function InputWithIcon({
  className,
  type,
  size,
  icon,
  iconPosition = 'inline-end',
  color,
  ref,
  inputClassName,
  ...props
}: InputProps & {
  icon: React.ReactNode;
  iconPosition?: 'inline-start' | 'inline-end';
  inputClassName?: string;
}) {
  return (
    <InputGroup
      className={className}
      color={color ?? 'default'}
      ref={ref}
      onClick={props.onClick}
    >
      <InputGroupInput
        type={type}
        className={cn('peer')}
        size={size}
        readOnly={props.readOnly}
        disabled={props.disabled}
        {...props}
      />
      <InputGroupAddon align={iconPosition}>{icon}</InputGroupAddon>
    </InputGroup>
  );
}

function InputLabel({
  label,
  htmlFor,
  infoIconWithTooltip,
  container,
  disabled,
  className,
}: Readonly<{
  label: React.ReactNode;
  htmlFor: string;
  infoIconWithTooltip?: string;
  container?: React.ComponentProps<typeof PopoverPortal>['container'];
  disabled?: boolean;
  className?: string;
}>) {
  return (
    <div className="flex items-center gap-1 pb-1">
      <Label
        variant="label01"
        color={'secondary'}
        htmlFor={htmlFor}
        disabled={disabled}
        className={cn(className)}
        data-disabled={disabled}
      >
        {label}
      </Label>
      {infoIconWithTooltip && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Information
              className={cn(
                'fill-icon-secondary',
                disabled && 'fill-text-disabled',
              )}
            />
          </TooltipTrigger>

          <TooltipContent className="z-tooltip" container={container}>
            {infoIconWithTooltip}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

function InputRoot({
  className,
  ...props
}: Readonly<React.ComponentProps<'div'>>) {
  return <div className={cn('grid', className)} {...props} />;
}

function InputHelperText({
  helperText,
  className,
  color = 'default',
  disabled,
}: Readonly<{
  helperText: string;
  className?: string;
  color?: null | 'default' | 'warning' | 'error';
  disabled?: boolean;
}>) {
  const icon =
    color === 'warning' ? (
      <WarningAlt className="fill-warning-main" />
    ) : (
      <Warning className="fill-error-main" />
    );
  return (
    <div className="inline-flex justify-start gap-1 pt-1">
      <span>{color === 'default' ? undefined : icon}</span>
      <p
        className={cn(
          typographyVariants({
            variant: 'helperText01',
            color: disabled ? 'disabled' : 'secondary',
          }),
          color === 'warning' && 'text-warning-main',
          color === 'error' && 'text-error-main',
          className,
        )}
      >
        {helperText}
      </p>
    </div>
  );
}

export {
  Input,
  InputWithCounter,
  InputWithIcon,
  InputLabel,
  InputRoot,
  InputHelperText,
  type InputProps as InputPropsType,
};
